import * as React from 'react';
import type { JSXElement } from '@fluentui/react-utilities';
import { useLexicalComposerContext } from '@fluentui-copilot/react-text-editor';
import type { LexicalEditor, EditorState } from '@fluentui-copilot/text-editor';
import type { GhostTextProps } from '../../GhostText';
import { $createGhostTextNode } from '../../GhostText';
import { GhostTextNode } from './GhostText.node';
import { GhostTextPluginBase } from '@fluentui-copilot/chat-input-plugins';

export type GhostTextPluginProps = {
  id: string;
  $getGhostText: (
    editor: LexicalEditor,
    editorState: EditorState,
    prevEditorState: EditorState,
  ) => Promise<string | undefined>;
  ghostTextProps?: GhostTextProps;
  // Whether or not the ghost text should count as text inside the input for submitting and character count
  exposeText?: boolean;
  allowCompletion?: boolean;
};

export function GhostTextPlugin(props: GhostTextPluginProps): JSXElement | null {
  'use no memo';

  const { $getGhostText, id, ghostTextProps, exposeText, allowCompletion } = props;
  const [editor] = useLexicalComposerContext();

  const [ghostTextBase, setGhostTextBase] = React.useState<GhostTextPluginBase<GhostTextProps> | null>(null);

  React.useEffect(
    () => {
      setGhostTextBase(
        new GhostTextPluginBase(
          editor,
          id,
          $getGhostText,
          GhostTextNode,
          $createGhostTextNode,
          ghostTextProps,
          exposeText,
          allowCompletion,
        ),
      );

      return () => {
        setGhostTextBase(prev => {
          prev?.cleanup();
          return null;
        });
      };
    },
    // We can update $getGhostText, $createGhostTextNode, ghostTextProps, exposeText, and allowCompletion without remounting
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editor, id],
  );

  React.useEffect(() => {
    ghostTextBase?.setAllowCompletion(allowCompletion);
  }, [allowCompletion, ghostTextBase]);

  React.useEffect(() => {
    ghostTextBase?.setComponentProps(ghostTextProps);
  }, [ghostTextBase, ghostTextProps]);

  React.useEffect(() => {
    ghostTextBase?.setExposeText(exposeText);
  }, [exposeText, ghostTextBase]);

  React.useEffect(() => {
    ghostTextBase?.setGetGhostText($getGhostText);
  }, [$getGhostText, ghostTextBase]);

  return null;
}
